import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import CardImage from "@/components/CardImage";
import { useAuth } from "@/context/AuthContext";
import ReviewerLockdown from "@/components/ReviewerLockdown";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PAGE_SIZE = 25;

export default function AdminDestinationsListPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [vibe, setVibe] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ states: [], countries: [], vibes: [] });
  // Item 5 — bulk-select state (missing from prior commit 190eb23 — caused
  // ReferenceError: selectedIds is not defined on /admin/destinations)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (user === null) return;
    if (user === false || (user.role !== "admin" && user.role !== "reviewer")) { nav("/auth"); }
  }, [user, nav]);

  const loadFacets = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/destinations/facets");
      setFacets(data);
    } catch (e) {
      console.error("[AdminDestinations] facets failed", e);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, page };
      if (q.trim()) params.q = q.trim();
      if (state) params.state = state;
      if (country) params.country = country;
      if (vibe) params.vibe = vibe;
      const { data } = await api.get("/admin/destinations", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error("[AdminDestinations] list failed", e);
      toast.error("Failed to load destinations");
    } finally {
      setLoading(false);
    }
  }, [q, state, country, vibe, page]);

  useEffect(() => { loadFacets(); }, [loadFacets]);

  // Debounce search input; other filters apply immediately.
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const onDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? This removes the card from the Explore feed on next refresh.`)) return;
    try {
      await api.delete(`/admin/destinations/${row.id}`);
      toast.success("destination deleted");
      load();
    } catch (e) {
      console.error("[AdminDestinations] delete failed", e);
      toast.error("Delete failed — try again");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Item 5 — clear selection whenever filters/page change (row ids on screen change)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [q, state, country, vibe, page]);

  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));
  const selectAllIndeterminate = someVisibleSelected && !allVisibleSelected;

  // Native <input> doesn't accept the indeterminate flag as a prop.
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = selectAllIndeterminate;
  }, [selectAllIndeterminate]);

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const onBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkConfirmOpen(false);
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/admin/destinations/bulk-delete", { ids });
      const failed = data.failed?.length || 0;
      if (failed > 0) {
        toast.error(`Deleted ${data.deleted}, but ${failed} failed`);
      } else {
        toast.success(`Deleted ${data.deleted} destination${data.deleted === 1 ? "" : "s"}`);
      }
      clearSelection();
      load();
    } catch (e) {
      console.error("[AdminDestinations] bulk-delete failed", e);
      toast.error("Bulk delete failed — try again");
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div data-testid="admin-destinations-list-page" className={"min-h-screen w-full bg-tb-navy-deep text-tb-cream " + (user?.role === "reviewer" ? "reviewer-locked" : "")}>
      <ReviewerLockdown />
      <header className="bg-tb-navy text-tb-gold border-b-2 border-tb-gold/70 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-destinations-back"
            onClick={() => nav("/admin")}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-navy-deep border-2 border-tb-gold/50 font-hand text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> admin
          </button>
          <h1 className="font-serif-it text-2xl">Destinations</h1>
        </div>
        <button
          data-testid="admin-destinations-create-btn"
          onClick={() => nav("/admin/destinations/new")}
          className="flex items-center gap-1 px-4 py-2 rounded-full bg-tb-gold text-tb-navy border-2 border-tb-gold font-hand text-sm tb-tap"
        >
          <Plus className="w-4 h-4" /> new destination
        </button>
      </header>

      <div className="p-6 max-w-6xl mx-auto space-y-4">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="relative col-span-1 md:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tb-navy/40" />
            <input
              data-testid="admin-destinations-search"
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
              placeholder="Search by name…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-tb-cream text-tb-navy border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
            />
          </label>
          <select
            data-testid="admin-destinations-filter-state"
            value={state}
            onChange={(e) => { setPage(1); setState(e.target.value); }}
            className="px-3 py-2 rounded-lg bg-tb-cream text-tb-navy border-2 border-tb-cream-3 font-ui text-sm"
          >
            <option value="">Any state</option>
            {facets.states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            data-testid="admin-destinations-filter-country"
            value={country}
            onChange={(e) => { setPage(1); setCountry(e.target.value); }}
            className="px-3 py-2 rounded-lg bg-tb-cream text-tb-navy border-2 border-tb-cream-3 font-ui text-sm"
          >
            <option value="">Any country</option>
            {facets.countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            data-testid="admin-destinations-filter-vibe"
            value={vibe}
            onChange={(e) => { setPage(1); setVibe(e.target.value); }}
            className="px-3 py-2 rounded-lg bg-tb-cream text-tb-navy border-2 border-tb-cream-3 font-ui text-sm"
          >
            <option value="">Any vibe</option>
            {facets.vibes.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <div className="col-span-3 flex items-center justify-end gap-3 text-tb-cream/70 font-hand text-sm">
            {loading ? "loading…" : `${total} destination${total === 1 ? "" : "s"}`}
          </div>
        </div>

        {/* Item 5 — bulk-delete action bar (only when at least one row selected) */}
        {selectedIds.size > 0 && (
          <div
            data-testid="admin-destinations-bulk-bar"
            className="flex items-center justify-between gap-3 bg-tb-navy border-2 border-tb-gold rounded-xl px-4 py-3 sticky top-2 z-10"
          >
            <span className="font-hand text-tb-gold-bright">
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                data-testid="admin-destinations-bulk-clear"
                onClick={clearSelection}
                className="px-3 py-1.5 rounded-full bg-tb-navy-deep text-tb-cream/80 border border-tb-cream/30 font-hand text-sm"
              >
                Clear
              </button>
              <button
                data-testid="admin-destinations-bulk-delete"
                onClick={() => setBulkConfirmOpen(true)}
                disabled={bulkDeleting}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-tb-coral text-white font-hand text-sm tb-tap disabled:opacity-60"
              >
                <Trash2 className="w-4 h-4" />
                {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size} selected`}
              </button>
            </div>
          </div>
        )}

        <AlertDialog open={bulkConfirmOpen} onOpenChange={(v) => { if (!v) setBulkConfirmOpen(false); }}>
          <AlertDialogContent data-testid="admin-destinations-bulk-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {selectedIds.size} destination{selectedIds.size === 1 ? "" : "s"}?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the selected rows from the catalog. Can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onBulkDelete} className="bg-tb-coral hover:brightness-110">
                Yes, delete {selectedIds.size}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Table */}
        <div data-testid="admin-destinations-table" className="bg-tb-cream text-tb-navy rounded-xl border-2 border-tb-cream-3 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-tb-cream-2 text-tb-navy/70 font-hand text-left">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    data-testid="admin-destinations-select-all"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    className="w-4 h-4 accent-tb-navy cursor-pointer"
                    aria-label="Select all on page"
                  />
                </th>
                <th className="px-4 py-3"></th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3">State</th>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center font-hand text-tb-navy/60">
                  no destinations match those filters
                </td></tr>
              ) : rows.map((row) => (
                <tr
                  key={row.id}
                  data-testid={`admin-destination-row-${row.id}`}
                  className={`border-t border-tb-cream-3 ${selectedIds.has(row.id) ? "bg-tb-gold/10" : ""}`}
                >
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      data-testid={`admin-destinations-select-row-${row.id}`}
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleRow(row.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 accent-tb-navy cursor-pointer"
                      aria-label={`Select ${row.name}`}
                    />
                  </td>
                  <td className="px-4 py-2">
                    {(() => {
                      // Same 3-source fallback used at read time everywhere
                      // else on the site: stay22_photos[0] holds the
                      // Booking.com thumb the Stay22 backfill fetched, and
                      // is populated on ~99% of rows — `featured_image` /
                      // `images[0]` are the older backup paths.
                      const trusted = (u) => {
                        if (!u || typeof u !== "string") return null;
                        if (u.toLowerCase().includes("placehold")) return null;
                        return u;
                      };
                      const thumb =
                        trusted(row.featured_image)
                        || trusted((row.stay22_photos || [])[0])
                        || trusted((row.images || [])[0]);
                      return (
                        <div className="w-12 h-12 rounded overflow-hidden">
                          <CardImage
                            card={{ ...row, featured_image: thumb, images: [] }}
                            className="w-full h-full"
                            showPlaceholderText={false}
                            imgClassName="w-full h-full object-cover"
                            alt=""
                            loading="lazy"
                          />
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2 font-medium">{row.name}</td>
                  <td className="px-4 py-2">{row.city}</td>
                  <td className="px-4 py-2">{row.state || "—"}</td>
                  <td className="px-4 py-2">{row.country}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    <button
                      data-testid={`admin-destination-edit-${row.id}`}
                      onClick={() => nav(`/admin/destinations/${row.id}`)}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs tb-tap"
                    >
                      <Pencil className="w-3 h-3"/> edit
                    </button>
                    <button
                      data-testid={`admin-destination-delete-${row.id}`}
                      onClick={() => onDelete(row)}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 text-tb-navy/70 font-hand text-xs tb-tap"
                    >
                      <Trash2 className="w-3 h-3"/> delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between font-hand text-sm text-tb-cream/70">
          <button
            data-testid="admin-destinations-prev-page"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-4 py-2 rounded-full bg-tb-navy border-2 border-tb-gold/50 text-tb-gold disabled:opacity-40"
          >
            prev
          </button>
          <span>page {page} of {totalPages}</span>
          <button
            data-testid="admin-destinations-next-page"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 rounded-full bg-tb-navy border-2 border-tb-gold/50 text-tb-gold disabled:opacity-40"
          >
            next
          </button>
        </div>
      </div>
    </div>
  );
}
